import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Play,
  Send,
  MapPin,
  Briefcase,
  Clock,
  User,
  FileText,
  CheckCircle,
  Video,
  Sparkles,
  Film,
  Palette,
  Wand2,
  Monitor,
  Home,
  Building2,
  ChevronRight,
  ExternalLink,
  Youtube,
  AlertTriangle,
  Calendar,
  Bot,
  Facebook,
  Instagram,
  Mail,
  Phone,
  MessageCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { cubeLogo } from "../data/mock";

// Golpo Hok Logo
const GOLPO_HOK_LOGO = "https://res.cloudinary.com/drcoefnzk/image/upload/v1768863847/golpo_hok_logo_lpziod.png";

// Hero Video
const HERO_VIDEO_ID = "jhrQj-TaW6g";

// Showcase Reels
const showcaseReels = [
  { id: "reel-1", youtubeId: "rDnyn1kVvl8", title: "Reel Edit 1" },
  { id: "reel-2", youtubeId: "IIZmWR_albY", title: "Reel Edit 2" },
  { id: "reel-3", youtubeId: "C5P58q8WrUE", title: "Reel Edit 3" },
  { id: "reel-4", youtubeId: "bNmK2ZFC5Pg", title: "Reel Edit 4" },
  { id: "reel-5", youtubeId: "B5YB2vTLhgw", title: "Reel Edit 5" },
  { id: "reel-6", youtubeId: "8dg7rmJC0z0", title: "Reel Edit 6" }
];

// Skills Required (Updated)
const requiredSkills = [
  { name: "Adobe Premiere Pro", icon: Film, level: "Advanced" },
  { name: "After Effects", icon: Wand2, level: "Expert" },
  { name: "DaVinci Resolve", icon: Palette, level: "Advanced" },
  { name: "Caption Generation", icon: FileText, level: "Advanced" },
  { name: "AI Image & Video Gen", icon: Bot, level: "Advanced" },
  { name: "Sound Design", icon: Video, level: "Intermediate" }
];

// What You'll Do
const responsibilities = [
  "Edit full-length podcast episodes (45-90 mins)",
  "Create engaging short-form reels for social media",
  "Add motion graphics, lower thirds, and visual effects",
  "Color grade footage for consistent brand look",
  "Sync audio and add sound effects/music",
  "Meet weekly deadlines consistently"
];

// Perks
const perks = [
  { icon: Building2, text: "Office in Dum Dum, Kolkata" },
  { icon: Clock, text: "10 AM - 7 PM" },
  { icon: Calendar, text: "Mon to Sat" },
  { icon: Briefcase, text: "Full-time Only" }
];

const YOUTUBE_CHANNEL = "https://www.youtube.com/@thesumantadey";
const FACEBOOK_PAGE = "https://www.facebook.com/golpohokpodcast";
const INSTAGRAM_PAGE = "https://www.instagram.com/thesumantadey";

// Reel Card Component
const ReelCard = ({ reel, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${reel.youtubeId}/0.jpg`;

  if (playing) {
    return (
      <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${reel.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
          title={reel.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="relative aspect-[9/16] rounded-xl overflow-hidden cursor-pointer group bg-black border border-white/10 hover:border-purple-500/50 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={reel.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-500 flex items-center justify-center shadow-lg"
        >
          <Play className="w-4 h-4 sm:w-5 sm:h-5 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default function GolpoHokHiringPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    experience: "",
    workMode: "",
    availability: "",
    expectation: "",
    portfolio: "",
    podcastSample: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [sameAsPhone, setSameAsPhone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // If phone changes and sameAsPhone is checked, update whatsapp too
    if (name === "phone" && sameAsPhone) {
      setFormData(prev => ({ ...prev, [name]: value, whatsapp: value }));
    }
  };

  const handleSameAsPhone = (checked) => {
    setSameAsPhone(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, whatsapp: prev.phone }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/hiring/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit application');
      }
      
      setSubmitted(true);
    } catch (err) {
      console.error("Application submission error:", err);
      setError(err.message || "Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Application Submitted!</h2>
          <p className="text-gray-400 mb-6">
            Thanks for applying to join the Golpo Hok team. We'll review your portfolio and get back to you soon.
          </p>
          <Link to="/">
            <Button className="bg-[#dc2626] hover:bg-[#b91c1c]">
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-8 sm:h-10 md:h-12" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a 
              href={FACEBOOK_PAGE}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white/5 hover:bg-blue-600/20 rounded-lg transition-all"
              title="Golpo Hok Facebook"
            >
              <Facebook className="w-4 h-4 text-blue-400" />
            </a>
            <a 
              href={INSTAGRAM_PAGE}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white/5 hover:bg-pink-600/20 rounded-lg transition-all"
              title="Sumanta Dey Instagram"
            >
              <Instagram className="w-4 h-4 text-pink-400" />
            </a>
            <a 
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-xs sm:text-sm font-medium transition-all"
            >
              <Youtube className="w-4 h-4" />
              <span className="hidden sm:inline">Watch Podcast</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 pb-8 sm:pb-12 px-4 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-64 sm:w-96 h-64 sm:h-96 bg-red-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Badge */}
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs sm:text-sm mb-4 sm:mb-6">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              We're Hiring
            </span>

            {/* Logo */}
            <div className="mb-4 sm:mb-6">
              <img 
                src={GOLPO_HOK_LOGO} 
                alt="Golpo Hok" 
                className="h-16 sm:h-20 md:h-24 mx-auto"
              />
            </div>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-2">Podcast by Sumanta Dey</p>
            
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mt-4 sm:mt-6 mb-3 sm:mb-4">
              Looking for <span className="text-[#dc2626]">Video Editors</span>
            </h2>
            
            <p className="text-sm sm:text-base text-gray-400 max-w-xl mx-auto mb-4 sm:mb-6 px-2">
              Join our creative team and edit one of Bengal's most engaging podcasts. 
              Create content that reaches millions.
            </p>

            {/* Full-time Notice */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl mb-4 sm:mb-6"
            >
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
              <span className="text-xs sm:text-sm font-semibold text-yellow-300">
                Full-Time Position Only • No Freelance/Part-time
              </span>
            </motion.div>

            {/* Quick Info */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-white/5 rounded-lg border border-white/10">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                <span className="text-[10px] sm:text-xs">Dum Dum, Kolkata</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-white/5 rounded-lg border border-white/10">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                <span className="text-[10px] sm:text-xs">10 AM - 7 PM</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-white/5 rounded-lg border border-white/10">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                <span className="text-[10px] sm:text-xs">Mon to Sat</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Video */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-black">
              {heroPlaying ? (
                <iframe
                  src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                  title="Golpo Hok Podcast"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div 
                  className="relative w-full h-full cursor-pointer group"
                  onClick={() => setHeroPlaying(true)}
                >
                  <img
                    src={`https://img.youtube.com/vi/${HERO_VIDEO_ID}/maxresdefault.jpg`}
                    alt="Golpo Hok Podcast"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-red-600 flex items-center justify-center shadow-lg"
                    >
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white ml-1" />
                    </motion.div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white font-semibold text-sm sm:text-base">Watch Full Podcast Episode</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* CTA Button */}
          <div className="text-center mt-6 sm:mt-8">
            <a href="#apply-form">
              <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 sm:px-8 py-3 sm:py-5 rounded-xl font-semibold text-sm sm:text-lg">
                Apply Now <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Showcase Reels Section */}
      <section className="px-4 py-10 sm:py-16 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-4">
                Our <span className="text-purple-400">Reel Edits</span>
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm max-w-lg mx-auto px-2">
                Watch these reels to understand our editing style, graphics, captions, and pacing. This is what we expect from you.
              </p>
            </motion.div>
          </div>

          {/* Reels Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
            {showcaseReels.map((reel, index) => (
              <ReelCard key={reel.id} reel={reel} index={index} />
            ))}
          </div>

          {/* Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-6 sm:mt-8 p-3 sm:p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl text-center"
          >
            <p className="text-xs sm:text-sm text-purple-300">
              <strong>Don't have a podcast portfolio?</strong> Download any of our podcast episodes from <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" className="underline">YouTube</a> and create sample reels to showcase your skills!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Skills Required Section */}
      <section className="px-4 py-10 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-4">
              Skills <span className="text-[#dc2626]">Required</span>
            </h2>
            <p className="text-gray-400 text-xs sm:text-sm">You should be proficient in these tools</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
            {requiredSkills.map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="p-3 sm:p-4 bg-[#141414] rounded-xl border border-white/10 text-center hover:border-purple-500/30 transition-all"
              >
                <skill.icon className="w-5 h-5 sm:w-7 sm:h-7 mx-auto mb-1.5 sm:mb-2 text-purple-400" />
                <p className="font-medium text-[10px] sm:text-xs leading-tight">{skill.name}</p>
                <span className="text-[9px] sm:text-[10px] text-gray-500">{skill.level}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Do Section */}
      <section className="px-4 py-10 sm:py-16 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-4">
              What You'll <span className="text-purple-400">Do</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-2 sm:gap-4">
            {responsibilities.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-[#141414] rounded-xl border border-white/10"
              >
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-gray-300">{item}</p>
              </motion.div>
            ))}
          </div>

          {/* Perks */}
          <div className="mt-6 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {perks.map((perk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10"
              >
                <perk.icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-purple-400" />
                <p className="text-[10px] sm:text-xs">{perk.text}</p>
              </motion.div>
            ))}
          </div>

          {/* Full-time Reminder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-6 sm:mt-8 p-4 sm:p-5 bg-red-500/10 border border-red-500/30 rounded-xl text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="font-bold text-red-400 text-sm sm:text-base">Important Notice</span>
            </div>
            <p className="text-xs sm:text-sm text-red-300">
              This is a <strong>Full-Time position only</strong>. We are NOT looking for freelancers or part-time editors. 
              Working hours: <strong>10 AM to 7 PM, Monday to Saturday</strong>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="apply-form" className="px-4 py-10 sm:py-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-4">
                Apply <span className="text-[#dc2626]">Now</span>
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm">Fill out the form below to apply</p>
            </motion.div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-[#141414] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-white/10"
          >
            {/* Name */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <User className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>

            {/* Email */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Email ID *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="your.email@example.com"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>

            {/* Phone Number */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>

            {/* WhatsApp Number */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                WhatsApp Number *
              </label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="sameAsPhone"
                  checked={sameAsPhone}
                  onChange={(e) => handleSameAsPhone(e.target.checked)}
                  className="w-4 h-4 rounded bg-white/5 border-white/10 text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="sameAsPhone" className="text-[10px] sm:text-xs text-gray-400 cursor-pointer">
                  Same as phone number
                </label>
              </div>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleInputChange}
                required
                disabled={sameAsPhone}
                placeholder="Enter your WhatsApp number"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors disabled:opacity-50"
              />
            </div>

            {/* Experience */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Experience *
              </label>
              <select
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                required
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm focus:outline-none focus:border-purple-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">Select experience level</option>
                <option value="fresher" className="bg-[#141414]">Fresher (0-1 year)</option>
                <option value="1-2" className="bg-[#141414]">1-2 years</option>
                <option value="2-4" className="bg-[#141414]">2-4 years</option>
                <option value="4+" className="bg-[#141414]">4+ years</option>
              </select>
            </div>

            {/* Work Mode */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Monitor className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Work Preference *
              </label>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <label 
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 rounded-lg sm:rounded-xl border cursor-pointer transition-all text-xs sm:text-sm ${
                    formData.workMode === "office" 
                      ? "bg-purple-500/20 border-purple-500" 
                      : "bg-white/5 border-white/10 hover:border-white/20"
                  }`}
                >
                  <input
                    type="radio"
                    name="workMode"
                    value="office"
                    checked={formData.workMode === "office"}
                    onChange={handleInputChange}
                    className="hidden"
                  />
                  <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Office</span>
                </label>
                <label 
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 rounded-lg sm:rounded-xl border cursor-pointer transition-all text-xs sm:text-sm ${
                    formData.workMode === "wfh" 
                      ? "bg-purple-500/20 border-purple-500" 
                      : "bg-white/5 border-white/10 hover:border-white/20"
                  }`}
                >
                  <input
                    type="radio"
                    name="workMode"
                    value="wfh"
                    checked={formData.workMode === "wfh"}
                    onChange={handleInputChange}
                    className="hidden"
                  />
                  <Home className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Work from Home</span>
                </label>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 sm:mt-2">
                <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline mr-1" />
                Office: Dum Dum, West Bengal • WFH requires proper editing system + stable WiFi
              </p>
            </div>

            {/* Availability */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Your Availability *
              </label>
              <select
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                required
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm focus:outline-none focus:border-purple-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">When can you start?</option>
                <option value="immediate" className="bg-[#141414]">Immediate</option>
                <option value="1-week" className="bg-[#141414]">Within 1 week</option>
                <option value="2-weeks" className="bg-[#141414]">Within 2 weeks</option>
                <option value="1-month" className="bg-[#141414]">Within 1 month</option>
              </select>
            </div>

            {/* Salary Expectation */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <FileText className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Salary Expectation (Monthly) *
              </label>
              <input
                type="text"
                name="expectation"
                value={formData.expectation}
                onChange={handleInputChange}
                required
                placeholder="e.g., ₹10,000 - ₹20,000"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>

            {/* Portfolio */}
            <div className="mb-4 sm:mb-5">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Video className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Portfolio Link
              </label>
              <input
                type="url"
                name="portfolio"
                value={formData.portfolio}
                onChange={handleInputChange}
                placeholder="Your portfolio website or Google Drive link"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 sm:mt-2">
                Share a Google Drive link with <strong>"Anyone with the link"</strong> access enabled.
              </p>
            </div>

            {/* Podcast Sample */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 text-gray-300">
                <Film className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Podcast Reel Edit Sample *
              </label>
              <input
                type="url"
                name="podcastSample"
                value={formData.podcastSample}
                onChange={handleInputChange}
                required
                placeholder="Google Drive link to your podcast reel edit"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 sm:mt-2">
                Share a Google Drive link with <strong>"Anyone with the link"</strong> access. If you don't have podcast work, download our podcast and create a sample reel.
              </p>
            </div>

            {/* Important Note */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg sm:rounded-xl">
              <p className="text-[10px] sm:text-xs text-yellow-300">
                <strong>Important:</strong> We only accept podcast editing portfolios. If you don't have podcast work, please download any episode from our <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" className="underline">YouTube channel</a> and create sample reels.
              </p>
            </div>

            {/* Support Helpline */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-500/10 border border-green-500/20 rounded-lg sm:rounded-xl flex items-center justify-between">
              <p className="text-[10px] sm:text-xs text-green-300">
                <strong>Need Help?</strong> Contact our support team
              </p>
              <a 
                href="https://wa.me/918017491858" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-white text-[10px] sm:text-xs font-medium transition-all"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-500/10 border border-red-500/20 rounded-lg sm:rounded-xl">
                <p className="text-[10px] sm:text-xs text-red-300">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-lg flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  Submit Application
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-6 sm:py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <img src={cubeLogo} alt="Cube Multimedia" className="h-6 sm:h-8" />
              <img src={GOLPO_HOK_LOGO} alt="Golpo Hok" className="h-6 sm:h-8" />
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <a href={FACEBOOK_PAGE} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 hover:bg-blue-600/20 rounded-lg transition-all" title="Facebook">
                <Facebook className="w-4 h-4 text-blue-400" />
              </a>
              <a href={INSTAGRAM_PAGE} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 hover:bg-pink-600/20 rounded-lg transition-all" title="Instagram">
                <Instagram className="w-4 h-4 text-pink-400" />
              </a>
              <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 hover:bg-red-600/20 rounded-lg transition-all" title="YouTube">
                <Youtube className="w-4 h-4 text-red-400" />
              </a>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] sm:text-xs text-gray-500">
            <span>© 2026 Cube Multimedia • Golpo Hok Podcast</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
